<template>
  <div class="address-input">
    <multiselect
      ref="multiselect"
      label="name"
      :group-values="optionType.length > 1 ? 'data' : null"
      :group-label="optionType.length > 1 ? 'type' : null"
      :allowEmpty="false"
      :placeholder="placeholder"
      :internal-search="false"
      :track-by="trackBy"
      :value="computedValue"
      :options="computedOptions"
      :disabled="disabled"
      :class="{ ...classes, 'empty-search': search === '' }"
      @input="handleInput"
      @search-change="handleSearchChange"
      @open="handleOpen"
      @close="isOpen = false"
    >
      <!-- Hide the default caret -->
      <template #caret>
        <div />
      </template>

      <template #placeholder>
        <div class="d-flex gap-2 align-items-center">
          <span class="__placeholder">
            {{ placeholder }}
          </span>
        </div>
      </template>

      <template #selection>
        <i class="icon-outline_search" />
      </template>

      <!-- If there is no option -->
      <template #noResult>
        <template v-if="isLoading || search === ''">
          <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
        </template>
        <template v-else> Tidak ada data </template>
      </template>

      <!-- Observer to get next page data -->
      <template v-if="optionType.includes(MASTER_DATA_ADDRESS_TYPE)" #afterList>
        <div v-if="isLoading || search === ''" class="px-2 py-2 d-flex gap-3 flex-column">
          <b-skeleton v-for="(i, idx) in ['100', '100']" :key="idx" :width="i" />
        </div>
        <div v-observe-visibility="handleElementObserved" style="height: 10px" />
      </template>

      <!-- Custom style of options -->
      <template #option="{ option }">
        <template v-if="option.$isLabel">
          <div class="__option-group">
            <div>
              {{ option.$groupLabel }}
            </div>
            <div>
              {{ option.$groupLabel === 'ALAMAT TERDAFTAR' ? ` (${totalCount})` : '' }}
            </div>
          </div>
        </template>
        <template v-else>
          <div class="__option-list">
            <div class="__option-icon">
              <i :class="iconMappings[option.type]" />
            </div>
            <div class="__option-content">
              <div class="__address-name">
                {{ option.name }}
              </div>
              <div class="__address-full-name">
                {{ option.fullName }}
              </div>
            </div>
          </div>
        </template>
      </template>
    </multiselect>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import Multiselect from 'vue-multiselect'

import { GET_ADDRESSES } from '@/services/api/trackingTemporary/address'
import { debounce } from '@/helpers/debounce'
import { GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE } from '../../lib/constants/types'

import { getPlaceDetail } from '@/services/api/google/placeDetail'
import { getPlacesAutocomplete } from '@/services/api/google/autocomplete'

export default {
  components: {
    Multiselect,
  },
  props: {
    map: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: Object,
      default: () => {
        return {
          id: undefined,
          name: '',
          fullName: '',
          type: '',
          lat: null,
          lng: null,
        }
      },
    },
    classes: {
      type: Object,
      default: () => ({}),
    },
    /**
     * To position the address result above other elements using a fixed
     * positioning, add the class 'fixed-input-wrapper' to the element you want the
     * address result to appear above.
     */
    fixed: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Cari nama lokasi, jalan, bangunan dll',
    },
    optionType: {
      type: Array,
      default: () => [GOOGLE_MAP_ADDRESS_TYPE, MASTER_DATA_ADDRESS_TYPE]
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      GOOGLE_MAP_ADDRESS_TYPE,
      MASTER_DATA_ADDRESS_TYPE,

      isLoading: false,
      isOpen: false,

      search: '',
      trackBy: 'name',

      page: 1,
      totalCount: 0,

      googleMapAddresses: [],
      masterAddresses: [],
      value: {},

      iconMappings: {
        [GOOGLE_MAP_ADDRESS_TYPE]: 'icon-outline_pin',
        [MASTER_DATA_ADDRESS_TYPE]: 'icon-outline_bookmark',
      },

      inputPosition: null,
    }
  },
  computed: {
    google: gmapApi,
    computedValue() {
      // set value null if there is no selected
      // so the placeholder will be shown
      if (!this.modelValue?.id) {
        return null
      }

      const newValue = { ...this.modelValue }

      // since key name is optional
      // set name from fullName to showing it in placeholder
      if (newValue.name === '') {
        newValue.name = newValue.fullName
      }

      return newValue
    },
    computedOptions() {
      let listOptionType = {
        GOOGLE: {
          type: 'DARI PETA',
          data: this.googleMapAddresses,
        },
        MEP_MASTER_DATA: {
          type: 'ALAMAT TERSIMPAN',
          data: this.masterAddresses,
        },
      }

      let options = []
      for (let index = 0; index < this.optionType.length; index++) {
        options.push(listOptionType[this.optionType[index]])
      }

      if (options.length > 1) return options

      return this.optionType.includes(GOOGLE_MAP_ADDRESS_TYPE) ? this.googleMapAddresses : this.masterAddresses
    },
  },
  watch: {
    inputPosition: {
      handler() {
        if (this.fixed) {
          this.updatePopupStyle()
        }
      },
      deep: true,
    },
  },
  mounted() {
    if (this.fixed) {
      this.startPositionCheck()
    }
  },
  methods: {
    // using PascalCase because it will be used by parent component
    HandleOpen() {
      let multiselect = this.$refs.multiselect
      multiselect.activate()
    },

    handleOpen() {
      this.isOpen = true

      // set input value with selected value
      let multiselect = this.$refs.multiselect
      multiselect.updateSearch(this.computedValue?.name || '')
    },

    async getData(type = 'initial') {
      this.isLoading = true

      // dont get data if there is no search query
      if (this.search === '') {
        this.isLoading = false
        return
      }

      if (type === 'initial') {
        const suggestions = await getPlacesAutocomplete(this.search)

        const googleMapAddresses = []
        suggestions.map((item) => {
            const newObj = {
              id: item.placePrediction.placeId,
              name: item.placePrediction.structuredFormat.mainText.text,
              fullName: item.placePrediction.structuredFormat.secondaryText.text,
              type: GOOGLE_MAP_ADDRESS_TYPE,
            }
            googleMapAddresses.push(newObj)
        })

        this.googleMapAddresses = googleMapAddresses
      } else if (type === 'next_page') {
        // set next page
        this.page += 1
      }

      if (!this.optionType.includes(MASTER_DATA_ADDRESS_TYPE)) {
        this.isLoading = false
        return
      }

      // search address from master data
      const filter = {
        ownership: ['Customer', 'Own'],
        pagination: {
          page: this.page,
          show: 5,
        },
        search: this.search,
      }

      const addresses = await this.$apollo.query({
        query: GET_ADDRESSES,
        fetchPolicy: 'no-cache',
        variables: { filter },
      })

      if (!addresses.data?.geofences?.geofences.data) return

      // set master data results to state
      addresses.data.geofences.geofences.data.forEach((address) => {
        let lat, lng

        if (address.type === 'C') {
          [lat, lng] = this.convertCircDataToLatLng(address.circData)
        } else if (address.type === 'P') {
          [lat, lng] = this.convertPolyDataToLatLng(address.polyData)
        }

        const newObj = {
          id: address.geofenceId.toString(),
          name: address.fenceName,
          fullName: address.address,
          type: MASTER_DATA_ADDRESS_TYPE,
          lat,
          lng,
        }

        this.masterAddresses.push(newObj)
      })

      this.totalCount = addresses.data.geofences.geofences.pagination.total
      this.isLoading = false
    },

    handleSearchChange: debounce(async function (search) {
      this.search = search

      // reset options if search is changing
      this.googleMapAddresses = []
      this.masterAddresses = []
      this.page = 1
      this.totalCount = 0

      await this.$nextTick()

      let type = 'initial'
      if (this.page > 1) {
        type = 'next_page'
      }

      await this.getData(type)
    }, 500),

    handleElementObserved(isVisible) {
      if (!isVisible) return

      // if there is any search query, dont get any data
      if (this.totalCount === this.masterAddresses.length) return
      this.getData('next_page')
    },

    async handleInput(selected) {
      const res = await getPlaceDetail(selected.id)
      selected = {
        ...selected,
        lat: res.latitude,
        lng: res.longitude,
      }

      this.$emit('update:modelValue', selected)
    },

    convertCircDataToLatLng(circData) {
      const regex = /<\((-?\d+\.\d+),(-?\d+\.\d+)\),[-+]?[0-9]*\.?[0-9]+>/
      const match = circData.match(regex)

      return [parseFloat(match[1]), parseFloat(match[2])]
    },

    convertPolyDataToLatLng(polyData) {
      // Remove unnecessary characters like parentheses
      const cleanedStr = polyData.replace(/[()]/g, '')

      // Split the string into individual latitude and longitude pairs
      const pairs = cleanedStr.split(',')

      // Extend bound from the lat lngs
      const bounds = new this.google.maps.LatLngBounds()
      for (let i = 0; i < pairs.length; i += 2) {
        const lat = parseFloat(pairs[i].trim())
        const lng = parseFloat(pairs[i + 1].trim())

        bounds.extend({ lat, lng })
      }

      // get the center of the bound
      return [bounds.getCenter().lat(), bounds.getCenter().lng()]
    },

    startPositionCheck() {
      const checkPosition = () => {
        if (this.$el) {
          const rect = this.$el.getBoundingClientRect()
          this.inputPosition = { top: rect.top, left: rect.left, bottom: rect.bottom }
        }
        requestAnimationFrame(checkPosition)
      }
      checkPosition()
    },

    updatePopupStyle() {
      if (!this.isOpen) return

      const detailSection = document.querySelector('.fixed-input-wrapper')
      if (!detailSection) return

      const isAbove = this.$refs.multiselect.$el.classList.contains('multiselect--above')
      const windowHeight = window.innerHeight
      const inputRect = this.$el.getBoundingClientRect()

      let multiselectContentWrapper = this.$refs.multiselect.$refs.list

      if (isAbove) {
        multiselectContentWrapper.style.bottom = `${windowHeight - this.inputPosition.top}px`
        multiselectContentWrapper.style.top = 'auto'
      } else {
        multiselectContentWrapper.style.top = `${inputRect.bottom}px`
        multiselectContentWrapper.style.bottom = 'auto'
      }

      multiselectContentWrapper.style.maxWidth = `${this.$refs.multiselect.$el.offsetWidth}px`
      multiselectContentWrapper.style.position = 'fixed'
      multiselectContentWrapper.style.left = `${this.inputPosition.left}px`
    },
  },
}
</script>

<style lang="scss" scoped>
.address-input {
  width: 100%;

  &__input-wrapper {
    display: flex;
  }

  &__clear-icon {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    right: 10px;
    top: 12px;
    font-size: 16px;
  }

  :deep(.multiselect) {
    .multiselect__tags {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-top: 10.5px !important;
      padding-bottom: 10.5px !important;

      i {
        font-size: 16px;

        &:before {
          color: $mep-color-text-200;
        }
      }

      input {
        padding: 0px;
        color: $mep-color-text-200;

        &::placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
        }
      }

      .multiselect__placeholder {
        .__placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
        }
      }
    }

    .multiselect__content {
      max-width: 300px;
    }

    .multiselect__element:not([role='option']) .multiselect__option--disabled {
      background-color: #fafcff !important;
      color: $mep-color-text-400 !important;
      border-top: 1px solid $mep-primary-100;
      border-bottom: 1px solid $mep-primary-100;
    }

    .multiselect__element {
      .__option-group {
        display: flex;
      }

      .__option-list {
        display: flex;
        overflow-wrap: anywhere;
        gap: 8px;

        .__option-icon {
          min-width: 24px;
          max-height: 24px;
          border-radius: 24px;
          background-color: $cyan-100;
          color: $mep-color-text-400;
          display: flex;
          align-items: center;
          justify-content: center;

          i {
            font-size: 16px;
          }
        }

        .__option-content {
          text-wrap: wrap;

          .__address-name {
            font-weight: 600;
            margin-bottom: 4px;
          }

          .__address-full-name {
            font-weight: 400;
          }
        }
      }
    }

    &.multiselect--active {
      .multiselect__tags {
        border-radius: 5px 5px 0 0;
        border: none;
        outline: 2px solid #d6e0ff;
      }
    }

    &.empty-search {
      &.multiselect--active {
        .multiselect__tags {
          border-radius: 5px;
        }
      }

      .multiselect__content-wrapper {
        display: none !important;
      }
    }
  }
}
</style>
